'use client';

import type { FC } from 'react';

import DOMPurify from 'dompurify';
import { SettingsIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@repo/ui/components/common';
import { cn } from '@repo/utils/common';

import type { MachineGroup, MachineSubgroup } from '@/shared/types/machine.types';

import { MachineGroupCardButton } from '@/components/machine/MachineGroupCardButton';
import { Picture } from '@/components/Picture';

interface MachineGroupCardProps {
  className?: string;
  isSelected?: boolean;
  group: MachineGroup;
  selectedSubgroup?: MachineSubgroup | null;
  onClick: () => void;
  onSubgroupClick: (subGroup: MachineSubgroup) => void;
}

export const MachineGroupCard: FC<MachineGroupCardProps> = ({
  className,
  isSelected = false,
  group,
  selectedSubgroup,
  onClick,
  onSubgroupClick,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'relative flex flex-1 flex-col rounded-sm transition-all duration-300',
        {
          'bg-primary text-white': isSelected,
          'bg-[#F3F3F3] text-gray-900': !isSelected,
        },
        className,
      )}
    >
      <button
        className="bg-accent focus-visible:ring-ring/50 relative h-80 cursor-pointer overflow-hidden rounded-t-sm focus-visible:ring-3"
        type="button"
        onClick={onClick}
      >
        {group.picture && (
          <Picture
            picture={group.picture}
            className="h-full w-full overflow-hidden"
            imgClassName="w-full h-full object-cover"
            sizes="(max-width: 768px) 100vw, 50vw"
            loading="lazy"
          />
        )}
      </button>

      <div className="flex flex-1 flex-col p-4 md:p-8">
        <div
          className={cn('flex-1', {
            'text-white': isSelected,
            'text-gray-600': !isSelected,
          })}
        >
          <h2 className="mb-4 text-3xl font-bold tracking-wide">{group.title}</h2>
          <div
            className="mb-8 flex-1 text-sm leading-relaxed"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(group.description ?? '') }}
          />
        </div>

        <div className="mb-8 flex flex-wrap gap-2">
          {group.subgroups.map((subgroup) => (
            <MachineGroupCardButton
              key={subgroup.id}
              isSelected={isSelected}
              isActive={selectedSubgroup?.id === subgroup.id}
              onClick={() => onSubgroupClick(subgroup)}
            >
              {subgroup.title}
            </MachineGroupCardButton>
          ))}
        </div>

        {isSelected ? (
          <Button
            className="w-full border-2 border-white bg-transparent py-3 font-medium text-white uppercase hover:bg-white hover:text-red-600 focus-visible:border-2 focus-visible:border-white focus-visible:ring-3 focus-visible:ring-white/50"
            variant="outline"
            onClick={onClick}
          >
            <XIcon />
            {t('machine.closeSection')}
          </Button>
        ) : (
          <Button
            variant="secondary"
            className="w-full py-3 font-medium uppercase focus-visible:ring-3 focus-visible:ring-black/30"
            data-testid="machine-group-configure"
            onClick={onClick}
          >
            <SettingsIcon />
            {t('machine.configure')}
          </Button>
        )}
      </div>
    </div>
  );
};
